import type { SponsorModule } from '@racemap/sdk/schema/events';
import { OneMinuteInMillis } from '@racemap/utilities/consts/time';
import { useEffect, useMemo, useState } from 'react';

export const ROTATION_INTERVAL_MS = OneMinuteInMillis;

export interface LogoBox {
  maxWidth: number;
  maxHeight: number;
}

export interface NaturalSize {
  width: number;
  height: number;
}

/** Width an image renders at inside a contain-box, matching `object-fit: contain`. */
export function renderedWidth(size: NaturalSize, box: LogoBox): number {
  if (size.width <= 0 || size.height <= 0) return 0;
  return Math.min(box.maxWidth, box.maxHeight * (size.width / size.height));
}

/** Longest prefix of `order` that fits into `available`. */
export function fitPrefix<T>(
  order: ReadonlyArray<T>,
  widthOf: (item: T) => number,
  gap: number,
  available: number,
): Array<T> {
  if (available <= 0) return [];

  const window: Array<T> = [];
  let usedWidth = 0;

  for (const item of order) {
    const needed = window.length > 0 ? widthOf(item) + gap : widthOf(item);
    if (usedWidth + needed > available) break;
    window.push(item);
    usedWidth += needed;
  }

  // A logo wider than the container is shown clipped rather than skipped, so the
  // rotation always advances instead of stalling on an empty frame.
  if (window.length === 0 && order.length > 0) return [order[0]];

  return window;
}

interface LogoLike {
  id: string;
  imageUrls?: { logo?: string } | null;
  pinned?: boolean;
}

export const UNMEASURED: NaturalSize = { width: 0, height: 0 };

/**
 * Display order of a frame. Centred rows put the pinned logos in the middle and split
 * the rest around them in list order; rows anchored to a side keep list order.
 */
export function centerPinned<T extends LogoLike>(
  frame: ReadonlyArray<T>,
  alignment: SponsorModule['alignment'],
): Array<T> {
  if (alignment === 'left' || alignment === 'right') return [...frame];

  const pinned = frame.filter((logo) => logo.pinned);
  const rest = frame.filter((logo) => !logo.pinned);
  const half = Math.ceil(rest.length / 2);
  return [...rest.slice(0, half), ...pinned, ...rest.slice(half)];
}

/**
 * Identity of a logo list by content rather than by array identity — the store
 * hands back a fresh array on every render. Order-, url- and pin-sensitive, so a
 * reorder, a re-uploaded logo or a toggled pin in the editor all restart the cycle.
 */
function contentKey<T extends LogoLike>(logos: ReadonlyArray<T> | null | undefined): string {
  return (
    logos
      ?.map((logo) => `${logo.id}:${logo.imageUrls?.logo ?? ''}:${logo.pinned ? 'pinned' : ''}`)
      .join(',') ?? ''
  );
}

/**
 * Whether measurement is still outstanding for any logo.
 *
 * An unmeasured logo has width 0, which `fitPrefix` would read as free and pack
 * into the window. Callers must hold off until this is false. A logo whose image
 * failed to load counts as settled — one broken URL must not hide the whole row.
 */
export function sizesPending<T extends LogoLike>(
  logos: ReadonlyArray<T> | null | undefined,
  sizes: Record<string, NaturalSize>,
): boolean {
  if (logos == null || logos.length === 0) return false;
  return logos.some((logo) => sizes[logo.id] == null);
}

/** Natural sizes of the logo images, keyed by logo id. */
export function useNaturalSizes<T extends LogoLike>(
  logos: ReadonlyArray<T> | null | undefined,
): Record<string, NaturalSize> {
  const [sizes, setSizes] = useState<Record<string, NaturalSize>>({});

  const logoKey = contentKey(logos);

  useEffect(() => {
    if (logos == null || logos.length === 0) {
      setSizes({});
      return;
    }

    let cancelled = false;
    Promise.all(
      logos.map(async (logo) => [logo.id, await getNaturalSize(logo.imageUrls?.logo)] as const),
    ).then((measured) => {
      if (!cancelled) setSizes(Object.fromEntries(measured));
    });

    return () => {
      cancelled = true;
    };
  }, [logoKey]);

  return sizes;
}

function getNaturalSize(src: string | undefined): Promise<NaturalSize> {
  if (src == null) return Promise.resolve(UNMEASURED);

  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
    image.onerror = () => resolve(UNMEASURED);
    image.src = src;
  });
}

/**
 * The current frame of `rotationFrames`, advancing one frame per ROTATION_INTERVAL_MS
 * and looping once the cycle is complete. `fit` must be memoised by the caller.
 */
export function useRotatingWindow<T extends LogoLike>(
  logos: ReadonlyArray<T> | null | undefined,
  fit: (order: ReadonlyArray<T>) => Array<T>,
): Array<T> {
  const logoKey = contentKey(logos);
  const frames = useMemo(() => rotationFrames(logos ?? [], fit), [logoKey, fit]);

  const [frame, setFrame] = useState(0);
  useEffect(() => setFrame(0), [logoKey]);

  // Keyed on the boolean, not the frames, so a resize does not restart the interval
  const rotates = frames.length > 1;
  useEffect(() => {
    if (!rotates) return;
    const interval = setInterval(() => setFrame((current) => current + 1), ROTATION_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [rotates]);

  return frames[frame % frames.length];
}

/**
 * Every frame of one rotation cycle. Pinned logos lead each frame and reserve their
 * width first. The rest pack in list order, skipping any that do not fit, until each
 * has been shown once. A logo that never fits beside the pinned ones is left out.
 * Spare room is topped up from the start of the list, stopping at the first misfit
 * so narrow logos do not win every frame's leftover space.
 */
export function rotationFrames<T extends LogoLike>(
  logos: ReadonlyArray<T>,
  fit: (order: ReadonlyArray<T>) => Array<T>,
): Array<Array<T>> {
  const rotating = logos.filter((logo) => !logo.pinned);
  const pinnedFit = fit(logos.filter((logo) => logo.pinned));
  const packInto = (frame: Array<T>, candidates: ReadonlyArray<T>, skipMisfits: boolean) => {
    for (const logo of candidates) {
      if (frame.includes(logo)) continue;
      if (fit([...frame, logo]).length > frame.length) frame.push(logo);
      else if (!skipMisfits) return;
    }
  };

  const frames: Array<Array<T>> = [];
  let unseen = rotating;
  do {
    const frame = [...pinnedFit];
    packInto(frame, unseen, true);
    const shown = unseen.filter((logo) => frame.includes(logo));
    // No unseen logo fits beside the pinned ones: rotation cannot make progress
    if (shown.length === 0) {
      if (frames.length === 0) frames.push(frame);
      break;
    }
    unseen = unseen.filter((logo) => !shown.includes(logo));
    packInto(frame, rotating, false);
    frames.push(frame.sort((a, b) => logos.indexOf(a) - logos.indexOf(b)));
  } while (unseen.length > 0);

  return frames;
}
